import React from 'react';
import { useQuery } from 'react-query';
import { motion } from 'framer-motion';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';

import LoadingSpinner from '../components/LoadingSpinner';
import ContractInfo from '../components/ContractInfo';
import SummaryCards from '../components/SummaryCards';
import ReferralInviteCard from '../components/ReferralInviteCard';
import TitularCard from '../components/TitularCard';
import DependentsGrid from '../components/DependentsGrid';
import DashboardPaymentsTable from '../components/DashboardPaymentsTable';
import CoverageInfo from '../components/CoverageInfo';
import CardGenerator from '../components/CardGenerator';
import ErroLoadData from '../components/ErroLoadData';

const DashboardContainer = styled.div`
  max-width: 1200px;
  margin: 40px auto;
  padding: 0 20px;
  min-height: calc(100vh - 200px);

  @media (max-width: 768px) {
    margin: 16px 12px 24px;
    padding: 0;
  }
`;

const PageHeader = styled.div`
  text-align: center;
  margin-bottom: 40px;
  
  h1 {
    font-size: 32px;
    font-weight: 700;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 10px;
  }
  
  p {
    font-size: 18px;
    color: ${props => props.theme.colors.grayMedium};
  }

  @media (max-width: 768px) {
    margin-bottom: 20px;

    h1 {
      font-size: 24px;
    }

    p {
      font-size: 15px;
    }
  }
`;

const DashboardContent = styled.div`
  background: ${props => props.theme.colors.white};
  border-radius: 12px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const DashboardHeader = styled.div`
  background: ${props => props.theme.colors.lightBlue};
  padding: 30px;
  text-align: center;
  border-bottom: 1px solid #e2e8f0;
  
  h2 {
    font-size: 24px;
    font-weight: 600;
    color: ${props => props.theme.colors.primary};
    margin-bottom: 10px;
  }
  
  p {
    color: ${props => props.theme.colors.grayDark};
  }
`;

const ContentSection = styled.div`
  padding: 40px;
  
  @media (max-width: 768px) {
    padding: 14px;
  }
`;

const SectionTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 2px solid ${props => props.theme.colors.lightBlue};
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 40px;
  color: ${props => props.theme.colors.error};
`;

const getCodigoIndicacao = (dashboardData) => {
  const fontes = [
    dashboardData?.contrato,
    dashboardData?.titular,
    dashboardData?.cliente,
  ];

  for (const fonte of fontes) {
    if (!fonte || typeof fonte !== 'object') continue;

    const codigo = String(fonte.codigoIndicacao ?? '').trim();
    if (codigo) return codigo;
  }

  return '';
};

const Dashboard = () => {
  const { user, selectedContrato } = useAuth();
  const numeroContrato = selectedContrato.numero;

  const { data, isLoading, error, refetch } = useQuery(
    ['dashboard', user?.documento, selectedContrato?.numero],
    async () => {
      const response = await api.get('/cliente/dashboard');
      return response.data.data;
    },
    {
      enabled: !!user?.documento,
      staleTime: 2 * 60 * 1000,
      onError: () => toast.error('Erro ao carregar dados do dashboard. Verifique o contrato selecionado.')
    }
  );

  const containerVariants = { hidden: { opacity: 0, y: -20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.6, staggerChildren: 0.1 } } };
  const itemVariants = { hidden: { opacity: 0, y: -20 }, visible: { opacity: 1, y: 0, transition: { duration: 0.4 } } };

  if (isLoading) return <LoadingSpinner />;

  if (error) {
    return (
      <DashboardContainer>
        <ErroLoadData></ErroLoadData>
      </DashboardContainer>
    );
  }

  if (!data) {
    return (
      <DashboardContainer>
        <ErrorMessage>
          <h3>Dados não encontrados</h3>
          <p>Não foram encontradas informações para o seu contrato.</p>
        </ErrorMessage>
      </DashboardContainer>
    );
  }

  const { cliente, contrato, dependentes, resumo, pagamentos } = data;

  // Obter valor do boleto: tenta o mês corrente, senão o mais recente.
  const getCurrentMonthPaymentValue = (paymentsList) => {
    if (!paymentsList || paymentsList.length === 0) return null;
    const now = new Date();

    // 1. Tenta encontrar o pagamento do mês corrente
    const currentMonthPayment = paymentsList.find((p) => {
      if (!p?.vencimento) return false;
      const parts = p.vencimento.split('/'); // DD/MM/YYYY
      if (parts.length !== 3) return false;
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1;
      const year = parseInt(parts[2], 10);
      const d = new Date(year, month, day); // Interpreta a data no fuso horário local
      return d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
    });

    if (currentMonthPayment) {
      return currentMonthPayment.valor;
    }

    // 2. Se não encontrou, retorna o valor do boleto mais recente (o primeiro da lista, que já vem ordenada pela API)
    return paymentsList[0]?.valor || null;
  };

  const currentMonthValue = getCurrentMonthPaymentValue(pagamentos);
  const codigoIndicacao = getCodigoIndicacao(data);
  const totalIndicados = Number(data.qtdContratosIndicados) || 0;

  return (
    <motion.div variants={containerVariants} initial="hidden" animate="visible">
      <DashboardContainer>
        <motion.div variants={itemVariants}>
          <PageHeader>
            <h1>Área do Cliente</h1>
            <p>Gerencie seu plano assistencial funerário</p>
          </PageHeader>
        </motion.div>

        <motion.div variants={itemVariants}>
          <DashboardContent>
            {/* <DashboardHeader>
              <h2>Bem-vindo, {cliente.nome}</h2>
              <p>Contrato: {contrato.numero} | Plano: {contrato.plano}</p>
            </DashboardHeader> */}

            <ContentSection>
              <motion.div variants={itemVariants}>
                <SectionTitle>Informações do Contrato</SectionTitle>
                <ContractInfo contrato={contrato} currentMonthValue={currentMonthValue} cliente={cliente} />
              </motion.div>

              {codigoIndicacao ? (
                <motion.div variants={itemVariants}>
                  <ReferralInviteCard
                    codigoIndicacao={codigoIndicacao}
                    totalIndicados={totalIndicados}
                  />
                </motion.div>
              ) : null}

              <motion.div variants={itemVariants}>
                <SummaryCards resumo={resumo} currentMonthValue={currentMonthValue} />
              </motion.div>

              <motion.div variants={itemVariants}>
                <SectionTitle>Titular do Plano</SectionTitle>
                <TitularCard cliente={cliente} contrato={contrato} currentMonthValue={currentMonthValue} />
              </motion.div>

              <motion.div variants={itemVariants}>
                <SectionTitle>Dependentes</SectionTitle>
                <DependentsGrid
                  dependentes={dependentes}
                  refetch={refetch}
                  contrato={numeroContrato}
                />

                <div style={{ position: 'absolute', left: '-9999px', top: '-9999px' }}>
                  <CardGenerator membros={[]} contrato={numeroContrato} />
                </div>

              </motion.div>

              <motion.div variants={itemVariants}>
                <SectionTitle id="historico-pagamentos">Histórico de Pagamentos</SectionTitle>
                <DashboardPaymentsTable pagamentos={pagamentos} />
              </motion.div>

              <motion.div variants={itemVariants}>
                <SectionTitle>Informações Importantes</SectionTitle>
                <CoverageInfo cliente={cliente} />
              </motion.div>
            </ContentSection>
          </DashboardContent>
        </motion.div>
      </DashboardContainer>
    </motion.div>
  );
};

export default Dashboard;
